import {
  GOOGLE_TAG_MANAGER_NO_SCRIPT_SRC,
  GOOGLE_TAG_MANAGER_SCRIPT_ID,
  GOOGLE_TAG_MANAGER_SCRIPT_SRC,
} from './contants'
import type { CheckoutOrderDataLayerParams, GTMDataLayerAdditionalParams } from './types'
import { consoleLog } from '@/tools/console-log'

export const initGTM = async (gtmId: string): Promise<void> => {
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
  createGtmNoScript(gtmId)
  await loadGTMScript(gtmId)
}

const loadGTMScript = (gtmId: string): Promise<void> =>
  new Promise((resolve, reject) => {
    ;(function () {
      if (document.getElementById(GOOGLE_TAG_MANAGER_SCRIPT_ID)) {
        resolve()
        return
      }
      const src = `${GOOGLE_TAG_MANAGER_SCRIPT_SRC}?id=${gtmId}&l=dataLayer`
      const script = window.document.createElement('script')
      script.id = GOOGLE_TAG_MANAGER_SCRIPT_ID
      script.src = src
      script.async = true

      script.addEventListener(
        'load',
        () => {
          consoleLog.green('GTM', 'Loaded!')
          resolve()
        },
        { once: true }
      )
      script.addEventListener('error', () => reject(new Error('Failed to load gtm script')), {
        once: true,
      })
      document.head.appendChild(script)
    })()
  })

export const initGTMDataLayer = (): void => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(['set', 'ads_data_redaction', true])
}

export const createGtmNoScript = (gtmId: string): void => {
  const src = `${GOOGLE_TAG_MANAGER_NO_SCRIPT_SRC}?id=${gtmId}`

  const noScript = window.document.createElement('noscript')
  const iframe = window.document.createElement('iframe')
  iframe.setAttribute('height', '0')
  iframe.setAttribute('width', '0')
  iframe.setAttribute('style', 'display:none;visibility:hidden')
  iframe.setAttribute('src', src)
  noScript.appendChild(iframe)

  const container = window.document.querySelector('body')
  if (container) {
    container.insertAdjacentElement('afterbegin', noScript)
  }
}

const prepareCheckoutOrderDataLayerParams = ({ user, orderId }: CheckoutOrderDataLayerParams) => {
  //todo: https://glovoapp.atlassian.net/browse/WEBX-255
  const dataLayerProperties = {} as Partial<GTMDataLayerAdditionalParams>

  // Following google best practices, we need to add user id to each event
  if (user?.id) {
    dataLayerProperties['userId'] = user.id
  }

  if (orderId) {
    dataLayerProperties['orderId'] = orderId
    dataLayerProperties['isFirstOrder'] = user?.deliveredOrdersCount === 0

    if (user?.email) {
      dataLayerProperties['email'] = user.email
    }

    if (user?.phoneNumber?.number) {
      dataLayerProperties['phoneNumber'] = user.phoneNumber.number
    }
  }

  return dataLayerProperties
}

export const sendGTMEvent = (eventName: string) => {
  // TODO: Add user data to dataLayer
  // https://glovoapp.atlassian.net/browse/WEBX-255

  // TODO: Add order data to dataLayer
  // https://glovoapp.atlassian.net/browse/WEBX-256
  const checkoutOrderDataLayerParams = prepareCheckoutOrderDataLayerParams({})
  window.dataLayer.push({ event: eventName, ...checkoutOrderDataLayerParams })
  consoleLog.green('GTM', 'Event sent', { eventName, checkoutOrderDataLayerParams })
}

/**
 *
 * Retrieve UTM parameters from URL and push them to the dataLayer.
 * URL example: https://www.some.com/something-else/?utm_source=Google-PageM&utm_medium=Paid&utm_campaign=SEM-Something
 *
 */
export const setupUTMs = (): void => {
  const url = window.location.href
  const urlObj = new URL(url)
  const searchParams = urlObj.searchParams
  const UTMParams: Record<string, string> = {}

  searchParams.forEach((val, key) => {
    if (key.startsWith('utm_')) {
      UTMParams[key] = val
    }
  })

  if (Object.keys(UTMParams).length) {
    window.dataLayer.push({ event: 'initUTMParameters', ...UTMParams })
  }
}
